/**
 * JALRAKSHAK Water Quality Sensor Data & Parameters
 * Designed for future plug-and-play connection with ESP32 / IoT / MQTT backend.
 */

export const SENSORS_CONFIG = [
  {
    id: 'ph',
    name: 'pH Level',
    shortName: 'pH',
    unit: 'pH',
    currentVal: 7.35,
    minVal: 0,
    maxVal: 14,
    optimalMin: 6.5,
    optimalMax: 8.5,
    status: 'Optimal',
    statusClass: 'status-optimal',
    description: 'Measures hydrogen-ion activity. Optimal pH (6.5-8.5) is critical for aquatic organism survival and drinking water safety.',
    icon: 'water-drop',
    history: [7.2, 7.3, 7.32, 7.4, 7.38, 7.35, 7.36]
  },
  {
    id: 'do',
    name: 'Dissolved Oxygen',
    shortName: 'DO',
    unit: 'mg/L',
    currentVal: 6.8,
    minVal: 0,
    maxVal: 15,
    optimalMin: 5.0,
    optimalMax: 9.5,
    status: 'Good',
    statusClass: 'status-optimal',
    description: 'Amount of gaseous oxygen dissolved in water. Vital indicator of biological health; levels below 4.0 mg/L indicate hypoxia.',
    icon: 'activity',
    history: [6.4, 6.5, 6.7, 6.9, 6.8, 6.8, 6.8]
  },
  {
    id: 'temp',
    name: 'Water Temperature',
    shortName: 'Temp',
    unit: '°C',
    currentVal: 22.4,
    minVal: 0,
    maxVal: 45,
    optimalMin: 18.0,
    optimalMax: 26.0,
    status: 'Normal',
    statusClass: 'status-optimal',
    description: 'Influences dissolved oxygen capacity, chemical reaction rates, and metabolic speeds of aquatic organisms.',
    icon: 'thermometer',
    history: [21.8, 22.0, 22.1, 22.3, 22.5, 22.4, 22.4]
  },
  {
    id: 'turbidity',
    name: 'Turbidity (Clarity)',
    shortName: 'Turbidity',
    unit: 'NTU',
    currentVal: 3.2,
    minVal: 0,
    maxVal: 100,
    optimalMin: 0,
    optimalMax: 5.0,
    status: 'Clear',
    statusClass: 'status-optimal',
    description: 'Measure of relative water clarity caused by suspended sediments, microplastics, or algae blooms.',
    icon: 'eye',
    history: [4.1, 3.8, 3.5, 3.4, 3.3, 3.2, 3.2]
  },
  {
    id: 'nitrate',
    name: 'Nitrate (NO₃⁻)',
    shortName: 'Nitrate',
    unit: 'mg/L',
    currentVal: 1.6,
    minVal: 0,
    maxVal: 20,
    optimalMin: 0,
    optimalMax: 3.5,
    status: 'Safe',
    statusClass: 'status-optimal',
    description: 'Inorganic nutrient from agricultural runoff. Elevated concentrations (>5 mg/L) cause eutrophication and toxic algal blooms.',
    icon: 'flask',
    history: [1.8, 1.7, 1.7, 1.6, 1.6, 1.6, 1.6]
  },
  {
    id: 'orp',
    name: 'Oxidation-Reduction (ORP)',
    shortName: 'ORP',
    unit: 'mV',
    currentVal: 340,
    minVal: -500,
    maxVal: 800,
    optimalMin: 250,
    optimalMax: 450,
    status: 'Optimal',
    statusClass: 'status-optimal',
    description: 'Indicates the net oxidizing or reducing power of the water body, reflecting self-cleansing capacity.',
    icon: 'zap',
    history: [320, 325, 330, 335, 338, 340, 340]
  }
];

export const SIMULATION_SCENARIOS = {
  normal: {
    label: 'Normal Pristine Baseline',
    wqi: 89,
    status: 'OPTIMAL CONDITION',
    statusColor: 'var(--color-eco-green)',
    alert: 'All 6 critical water quality indices within WHO & CPCB standard ecological ranges. No active anomaly detected.',
    alertLevel: 'info',
    values: { ph: 7.35, do: 6.8, temp: 22.4, turbidity: 3.2, nitrate: 1.6, orp: 340 },
    asvAction: 'Routine Autonomous Grid Patrol • Speed: 1.8 kn • Waypoint 4/12'
  },
  industrial: {
    label: 'Simulate Industrial Effluent Spike',
    wqi: 48,
    status: 'ATTENTION REQUIRED',
    statusColor: 'var(--color-warning-amber)',
    alert: 'CRITICAL WARNING: Rapid pH depression (5.1 pH) & sudden Turbidity surge (24.8 NTU) detected at GPS Lat: 12.9348°N, Lon: 77.6742°E.',
    alertLevel: 'warning',
    values: { ph: 5.1, do: 3.2, temp: 26.8, turbidity: 24.8, nitrate: 7.4, orp: 180 },
    asvAction: 'Autonomous Redirection: Pinpointing Effluent Plume • High-Frequency Water Sampling Activated'
  },
  eutrophication: {
    label: 'Simulate Agricultural Runoff / Algal Bloom',
    wqi: 36,
    status: 'HAZARDOUS CONTAMINATION',
    statusColor: 'var(--color-danger-red)',
    alert: 'ALERT: Severe Nitrate Surge (14.2 mg/L) & Hypoxic Dissolved Oxygen drop (2.1 mg/L). Immediate ecosystem intervention required.',
    alertLevel: 'danger',
    values: { ph: 8.9, do: 2.1, temp: 28.5, turbidity: 42.0, nitrate: 14.2, orp: 110 },
    asvAction: 'Automated Waste Skimmer Deployed • Dispatching Telemetry Report to Watershed Authority'
  }
};
